import { useQuery } from '@apollo/client';
import makeStyles from '@mui/styles/makeStyles';
import { FC, useCallback, useEffect, useState } from 'react';

import { gengql } from '@/__generated__';
import { EnableOtpAuth } from '@/auth/components/SecurityEnableOtpAuth';
import {
  completeCognitoTotpEnrolment,
  releaseCognitoEnrolment,
  startCognitoTotpEnrolment,
} from '@/auth/utils/cognitoLogin';
import Button from '@/components/Button';
import Divider from '@/components/Divider';
import { borderColor } from '@/theme/colors';

import { CognitoTotpEnrolment } from './CognitoTotpEnrolment';

const useStyles = makeStyles({
  root: {
    textAlign: 'center',
  },
  button: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    marginTop: 25,
  },
  submitButton: {
    margin: '20px 15px 0 ',
  },
  title: {
    fontSize: 36,
  },
  divider: {
    backgroundColor: borderColor,
    margin: '20px 0 40px',
  },
  logo: {
    maxWidth: 300,
    maxHeight: 100,
    width: 'auto',
    height: 'auto',
  },
});

const GET_ABOUT_PAGE_LOGO_URL = gengql(/* GraphQL */ `
  query getEnrollOtpAuthAboutPageLogoUrl($groupId: String!) {
    aboutPageLogoURL(group_id: $groupId)
  }
`);

interface EnrollOtpAuthProps {
  /** The enforcing team, for its logo. The Cognito path has none. */
  groupId?: string;
  email: string;
  setOtp: (otp: string) => void;
  /** Set on the Cognito path (ENG-6103): enrol TOTP through Cognito, then call this. */
  onCognitoEnrolled?: () => void;
  /** Cognito path: the sign-in session lapsed mid-enrolment. */
  onCognitoExpired?: () => void;
}

const EnrollOtpAuth: FC<EnrollOtpAuthProps> = ({
  groupId,
  email,
  setOtp,
  onCognitoEnrolled,
  onCognitoExpired,
}) => {
  const s = useStyles();

  const { data } = useQuery(GET_ABOUT_PAGE_LOGO_URL, {
    variables: { groupId: groupId ?? '' },
    skip: !groupId,
  });
  const startCognito = useCallback(() => startCognitoTotpEnrolment(email), [email]);
  const handleCognitoExpired = useCallback(() => onCognitoExpired?.(), [onCognitoExpired]);
  // Leaving the screen abandons a half-done enrolment.
  const cognito = !!onCognitoEnrolled;
  useEffect(() => (cognito ? releaseCognitoEnrolment : undefined), [cognito]);
  const [enableOtpAuthOpen, setEnableOtpAuthOpen] = useState(false);

  const handleEnableOtpAuthOpen = useCallback(() => {
    setEnableOtpAuthOpen(true);
  }, []);

  // const handleEnableOtpAuthClose = useCallback(() => {
  //   onSubmit();
  // }, [onSubmit]);

  const groupLogoURL = data?.aboutPageLogoURL;

  const logo = groupLogoURL && <img alt="logo" src={groupLogoURL} className={s.logo} />;

  return (
    <div className={s.root}>
      {logo}

      {logo && <Divider className={s.divider} />}

      <h1 className={s.title}>Set up two-factor authentication</h1>

      <p>
        To protect your account and your team&apos;s data, each time you sign in to <br />
        your OnFrontiers account, you&apos;ll need your password and a verification <br />
        code we send to your mobile phone or authentication app or email.
      </p>

      {onCognitoEnrolled && enableOtpAuthOpen ? (
        <CognitoTotpEnrolment
          start={startCognito}
          verify={async (code) => {
            await completeCognitoTotpEnrolment(code);
          }}
          onEnrolled={onCognitoEnrolled}
          onExpired={handleCognitoExpired}
        />
      ) : (
        <div className={s.button}>
          <Button
            classes={{ root: s.submitButton }}
            onClick={handleEnableOtpAuthOpen}
            fullWidth={false}
          >
            Get Started
          </Button>
        </div>
      )}

      {!onCognitoEnrolled && (
        <EnableOtpAuth
          open={enableOtpAuthOpen}
          onCancel={() => setEnableOtpAuthOpen(false)}
          email={email}
          setOtp={setOtp}
        />
      )}
    </div>
  );
};

export default EnrollOtpAuth;
